<template>
    <div class="myshare-page">
        <section class="search-bar">
            <span class="search-label">搜索</span>
            <input v-model="keyword" class="search-input" type="text" placeholder="请输入关键词" />
        </section>
        <section class="share-list">
            <article v-for="item in items" :key="item.id" class="share-card">
                <div class="card-top">
                    <div class="share-title">
                        <template v-if="editingId === item.id">
                            <input v-model="editTitle" class="title-edit-input" type="text" maxlength="40" />
                        </template>
                        <template v-else>
                            {{ item.title }}
                        </template>
                    </div>
                    <button class="title-edit-btn" type="button" @click="toggleEditTitle(item)">
                        {{ editingId === item.id ? "保存" : "编辑" }}
                    </button>
                </div>

                <div class="card-body">
                    <div class="card-info">
                        <div class="info-row info-row--two">
                            <div class="info-item">
                                <span class="info-label">类别</span>
                                <template v-if="editingId === item.id">
                                    <select
                                        v-model="editCategoryId"
                                        class="category-edit-select"
                                        :disabled="categories.length === 0"
                                    >
                                        <option v-for="category in categories" :key="category.categoryId" :value="category.categoryId">
                                            {{ category.name }}
                                        </option>
                                    </select>
                                </template>
                                <template v-else>
                                    <span class="info-value">{{ item.categoryName }}</span>
                                </template>
                            </div>
                            <div class="info-item">
                                <span class="info-label">状态</span>
                                <span class="info-value">
                                    <span class="badge muted" :class="item.auditClass">{{ item.auditStatus }}</span>
                                </span>
                            </div>
                        </div>
                        <div class="info-row info-row--four">
                            <div class="info-item">
                                <span class="info-label">文件数</span>
                                <span class="info-value">{{ item.fileCount }}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">大小</span>
                                <span class="info-value">{{ item.size }}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">阅读数</span>
                                <span class="info-value">{{ item.traffic }}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">时间</span>
                                <span class="info-value">{{ item.sharedAt }}</span>
                            </div>
                        </div>
                        <div class="info-row info-row--one info-row--highlight">
                            <div class="info-item">
                                <span class="info-label">网盘地址</span>
                                <span class="info-value">{{ item.driveUrl }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-actions">
                    <button class="copy-btn" type="button" @click="copyShare(item)">
                        复制
                    </button>
                    <button class="delete-btn" type="button" @click="removeShare(item.id)">
                        删除
                    </button>
                </div>
            </article>

            <div ref="sentinel" class="sentinel"></div>
            <div v-if="loading" class="loading">Loading...</div>
            <div v-else-if="!hasMore" class="end">没有数据了!</div>
        </section>
        <AppFooter active="member" />
        <div v-if="copyToastVisible" class="copy-toast">复制成功</div>
    </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { Modal } from "ant-design-vue";

type ShareItem = {
    id: string;
    title: string;
    auditStatus: string;
    auditClass: string;
    categoryId?: number;
    categoryName?: string;
    category: string;
    driveUrl: string;
    sharedAt: string;
    fileCount: number;
    traffic: number;
    size: string;
};

const items = ref<ShareItem[]>([]);
const page = ref(0);
const loading = ref(false);
const hasMore = ref(true);
const sentinel = ref<HTMLElement | null>(null);
const editingId = ref<string | null>(null);
const editTitle = ref("");
const editCategoryId = ref<number | null>(null);
const keyword = ref("");
const pageSize = 10;
let searchTimer: ReturnType<typeof setTimeout> | null = null;
const copyToastVisible = ref(false);
let copyToastTimer: ReturnType<typeof setTimeout> | null = null;

type SourceAttr = {
    attrType?: number;
    attrData?: string;
};

type SourceItem = {
    sourceId?: string;
    title?: string;
    categoryId?: number;
    categoryName?: string;
    sourceType?: number;
    attrs?: SourceAttr[];
    createTime?: string;
    state?: number;
    pv?: number;
};

type CategoryItem = {
    categoryId: number;
    name: string;
};

const categories = ref<CategoryItem[]>([]);
const categoriesLoading = ref(false);

const loadCategories = async () => {
    if (categoriesLoading.value || categories.value.length > 0) return;
    categoriesLoading.value = true;
    try {
        const response = await $api_Category_GetCategorys({
            method: "POST",
        });
        if (response?.code === 1 && Array.isArray(response.data)) {
            categories.value = response.data;
        }
    } finally {
        categoriesLoading.value = false;
    }
};


const toggleEditTitle = async (item: ShareItem) => {
    if (editingId.value === item.id) {
        const newTitle = editTitle.value.trim();
        const categoryId = editCategoryId.value ?? item.categoryId ?? undefined;
        if (!newTitle) {
            editingId.value = null;
            editTitle.value = "";
            editCategoryId.value = null;
            return;
        }
        const sourceId = item.id;
        const response = await $api_Source_ModifySourceTitle({
            method: "POST",
            body: {
                title: newTitle,
                sourceId,
                categoryId,
            },
        });
        if (response?.code === 1) {
            item.title = newTitle;
            if (categoryId !== undefined) {
                item.categoryId = categoryId;
                const categoryName = categories.value.find((category) => category.categoryId === categoryId)?.name;
                if (categoryName) {
                    item.categoryName = categoryName;
                }
            }
            editingId.value = null;
            editTitle.value = "";
            editCategoryId.value = null;
        }
        return;
    }
    editingId.value = item.id;
    editTitle.value = item.title;
    editCategoryId.value = item.categoryId ?? null;
    loadCategories();
};

const copyShare = async (item: ShareItem) => {
    const text = `${item.title}\n${item.driveUrl}`;
    if (import.meta.client && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        showCopyToast();
        return;
    }
    if (!import.meta.client) return;
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    showCopyToast();
};

const showCopyToast = () => {
    copyToastVisible.value = true;
    if (copyToastTimer) {
        clearTimeout(copyToastTimer);
    }
    copyToastTimer = setTimeout(() => {
        copyToastVisible.value = false;
    }, 1500);
};

const removeShare = async (id: string) => {
    if (!id || !import.meta.client) return;
    Modal.confirm({
        title: "确认删除该分享吗？",
        okText: "确定",
        cancelText: "取消",
        width: 320,
        className: "myshare-confirm",
        okButtonProps: {
            style: {
                backgroundColor: "#f2694b",
                borderColor: "#f2694b",
                color: "#fff",
            },
        },
        async onOk() {
            const response = await $api_Source_DeleteSouceById({
                method: "GET",
                query: { sourceId: id },
            });
            if (response?.code === 1) {
                items.value = items.value.filter((item) => item.id !== id);
            }
        },
    });
};

const getDriveUrl = (attrs: SourceAttr[] | undefined) => {
    let list = attrs.filter(item => {
        // 返回一个布尔值，判断item是否符合条件
        return item.attrType > 2000 && item.attrType < 3000
    })
    if (!list || list.length == 0) return ""
    return list[0]?.attrData ?? ""
}

const mapSourceItem = (item: SourceItem): ShareItem => {
    const stateValue = item.state ?? 0;
    return {
        id: String(item.sourceId ?? ""),
        title: item.title ?? "",
        auditStatus: stateValue == 1 ? "已审核" : "未审核",
        auditClass: stateValue == 1 ? "audit-reviewed" : "audit-unreviewed",
        categoryId: item.categoryId,
        categoryName: item.categoryName,
        driveUrl: getDriveUrl(item.attrs),
        sharedAt: (item.createTime || "").replace("T", " ").slice(0, 16),
        fileCount: item.attrs?.length ?? 0,
        traffic: item.pv ?? 0,
        size: "-",
    };
};


const loadMore = async () => {
    if (loading.value || !hasMore.value) return;
    loading.value = true;
    const nextPage = page.value + 1;
    try {


        const data = await $api_Source_GetMySourceByPage({
            method: "POST",
            body: {
                pageNum: nextPage,
                pageSize,
                likeTitle: keyword.value || undefined,
                states: [0, 1, 2],
            },
        })
        const response = data as IApiResponse<any> | null;
        if (!response || response.code !== 1) {
            hasMore.value = false;
            return;
        }
        const payload = response.data;
        const list = payload?.list ?? [];
        const mapped = list.map(mapSourceItem);
        if (nextPage === 1) {
            items.value = mapped;
        } else {
            items.value.push(...mapped);
        }
        page.value = nextPage;
        const total = payload?.total ?? 0;
        const currentPage = payload?.currentNumber ?? nextPage;
        const size = payload?.pageSize ?? pageSize;
        hasMore.value = currentPage * size < total;
    }
    catch (error) {
        if (import.meta.client) {
            window.alert(error)
        }
    }
    finally {
        loading.value = false;
    }
};

let observer: IntersectionObserver | null = null;

onMounted(() => {
    loadCategories();
    loadMore();
    if (!sentinel.value) return;
    observer = new IntersectionObserver(
        (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                loadMore();
            }
        },
        { rootMargin: "120px" }
    );
    observer.observe(sentinel.value);
});

onBeforeUnmount(() => {
    observer?.disconnect();
    if (searchTimer) {
        clearTimeout(searchTimer);
        searchTimer = null;
    }
    if (copyToastTimer) {
        clearTimeout(copyToastTimer);
        copyToastTimer = null;
    }
});

watch(keyword, () => {
    if (searchTimer) {
        clearTimeout(searchTimer);
    }
    searchTimer = setTimeout(() => {
        page.value = 0;
        items.value = [];
        hasMore.value = true;
        loadMore();
    }, 300);
});
</script>

<style scoped>
:global(body) {
    background: #f9f6f2;
}

.myshare-page {
    --ink: #1f1e25;
    --muted: #6f6b77;
    --card: #ffffff;
    --shadow: rgba(240, 126, 96, 0.12);
    min-height: 100vh;
    padding: 18px 16px 100px;
    background:
        radial-gradient(circle at top, rgba(255, 219, 205, 0.8), transparent 60%),
        linear-gradient(180deg, #f9f6f2 0%, #ffffff 65%);
    font-family: "PingFang SC", "Helvetica Neue", Arial, sans-serif;
    color: var(--ink);
}

.page-header {
    margin-bottom: 16px;
}

.kicker {
    font-size: 12px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #f2694b;
    font-weight: 700;
}

.title {
    margin: 6px 0 4px;
    font-size: 22px;
    font-weight: 800;
}

.title-btn {
    border: none;
    background: transparent;
    padding: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
    text-align: left;
}

.title-input {
    width: 100%;
    border: 1px solid rgba(242, 105, 75, 0.3);
    border-radius: 10px;
    padding: 6px 10px;
    font-size: 20px;
    font-weight: 800;
    color: var(--ink);
    background: #fff;
    outline: none;
}

.title-input:focus {
    border-color: rgba(242, 105, 75, 0.7);
    box-shadow: 0 0 0 3px rgba(242, 105, 75, 0.12);
}

.subtitle {
    margin: 0;
    font-size: 13px;
    color: var(--muted);
}

.search-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #fff;
    border-radius: 14px;
    padding: 10px 12px;
    box-shadow: 0 10px 20px rgba(240, 126, 96, 0.12);
    margin-bottom: 14px;
}

.search-label {
    font-size: 12px;
    color: var(--muted);
    white-space: nowrap;
}

.search-input {
    flex: 1;
    border: 1px solid #f2e2db;
    border-radius: 10px;
    padding: 8px 10px;
    font-size: 13px;
    background: #fff8f5;
    color: var(--ink);
    outline: none;
}

.search-input:focus {
    border-color: rgba(242, 105, 75, 0.45);
    box-shadow: 0 0 0 3px rgba(242, 105, 75, 0.12);
}

.share-list {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.share-card {
    background: var(--card);
    border-radius: 16px;
    padding: 14px;
    box-shadow: 0 12px 24px var(--shadow);
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.card-top {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: flex-start;
}

.share-title {
    font-size: 16px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 8px;
}

.title-edit-btn {
    border: 1px solid #f3c7ba;
    background: #fff;
    color: #c24f2f;
    padding: 4px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
}

.title-edit-input {
    border: 1px solid rgba(242, 105, 75, 0.3);
    border-radius: 8px;
    padding: 4px 8px;
    font-size: 14px;
    font-weight: 700;
    color: var(--ink);
    background: #fff;
    outline: none;
}

.category-edit-select {
    border: 1px solid rgba(242, 105, 75, 0.3);
    border-radius: 8px;
    padding: 4px 8px;
    font-size: 13px;
    font-weight: 600;
    color: var(--ink);
    background: #fff;
    outline: none;
}


.audit-reviewed {
    background: #e8f8f0;
    color: #2f8f5c;
}

.audit-unreviewed {
    background: #fff3d4;
    color: #b26f12;
}

.card-body {
    display: flex;
}

.card-actions {
    display: flex;
    justify-content: center;
    gap: 12px;
}

.copy-btn {
    border: 1px solid #f3c7ba;
    background: #fff7f3;
    color: #c24f2f;
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
}

.delete-btn {
    border: 1px solid #f1d3d3;
    background: #fff5f5;
    color: #c74343;
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
}

.copy-toast {
    position: fixed;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    background: rgba(31, 30, 37, 0.9);
    color: #fff;
    padding: 8px 14px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.2);
    z-index: 30;
}

.card-info {
    display: grid;
    gap: 8px;
}

.info-row {
    display: grid;
    gap: 10px;
}

.info-row--two {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.info-row--three {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.info-row--four {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.info-row--one {
    grid-template-columns: minmax(0, 1fr);
}

.info-row--highlight {
    background: #fff2ec;
    border-radius: 12px;
    padding: 8px 10px;
}

.info-item {
    display: flex;
    align-items: center;
    gap: 8px;
}

.info-label {
    font-size: 12px;
    color: var(--muted);
    white-space: nowrap;
}

.info-value {
    font-size: 13px;
    color: var(--ink);
    word-break: break-all;
}

.sentinel {
    height: 1px;
}

.loading,
.end {
    text-align: center;
    font-size: 12px;
    color: var(--muted);
    padding-bottom: 12px;
}

:global(.myshare-confirm .ant-modal-confirm-btns) {
    display: flex;
    justify-content: center;
    gap: 12px;
}

@media (min-width: 768px) {
    .myshare-page {
        padding: 24px 22% 120px;
    }
}
</style>
